import React, { useMemo } from 'react';
import { Select } from 'antd';
import Datestamp from 'vl-common/src/components/Datestamp';
import { TableDataView, HeaderItemType } from 'vl-common/src/components/Table/TableDataView';

export interface RequestDetails {
  id: string;
  appt_id: string;
  date: Date;
  description: string;
  status: string;
}

interface CaseRequestsProps {
  requests: RequestDetails[];
  onStatusChange?: (status: string, r: RequestDetails) => void;
  onResendRequest?: (r: RequestDetails) => void;
  onChangeProvider?: (r: RequestDetails) => void;
  metaMisc: any;
}

export const CaseRequests: React.FC<CaseRequestsProps> = ({
  requests,
  onStatusChange,
  onResendRequest,
  onChangeProvider,
  metaMisc
}) => {
  const states = useMemo(
    () =>
      Object.fromEntries(
        metaMisc?.ireq_states.map(
          ({ ireq_state_code, ireq_state_desc }: { ireq_state_code: string; ireq_state_desc: string }) => [
            ireq_state_code,
            ireq_state_desc
          ]
        ) || []
      ),
    [metaMisc]
  );
  const internalStates = useMemo(() => {
    if (!metaMisc?.ireq_states) {
      return new Set();
    }

    return new Set(
      metaMisc.ireq_states
        .filter(({ ireq_state_internal }: { ireq_state_internal: string }) => ireq_state_internal === 'true')
        .map(({ ireq_state_code }: { ireq_state_code: string }) => ireq_state_code)
    );
  }, [metaMisc]);

  const ColumHeaders = useMemo(
    () => [
      {
        display: 'Request',
        dataSourceKey: 'id'
      },
      {
        display: 'Initiation Date',
        dataSourceKey: 'date',
        columnDataFormat: ({ data: date }: { data: Date }) => (
          <div style={{ maxWidth: 280 }}>
            <Datestamp date={date} format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57" />
          </div>
        )
      },
      {
        display: 'Appointment',
        dataSourceKey: 'appt_id',
        columnDataFormat: ({ data: id }: { data: string }) => <span>{id}</span>
      },
      {
        display: 'Status',
        dataSourceKey: 'status',
        columnDataFormat: (request: any) => {
          const { data, entireData } = request;

          return (
            <Select
              data-testid="request-status-select"
              style={{ minWidth: 280, maxWidth: 320 }}
              value={data}
              virtual={false}
              onChange={(value) => {
                if (value && onStatusChange) onStatusChange(value, entireData);
              }}
            >
              {Object.entries(states).map(([value, label]) => (
                <Select.Option key={value} value={value} disabled={internalStates.has(value)}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          );
        }
      },
      {
        display: 'Description',
        dataSourceKey: 'description',
        columnDataFormat: (value: { data: string }) => {
          return <span style={{ minWidth: '500px' }}>{value.data}</span>;
        }
      },
      {
        display: 'Actions',
        isAction: true,
        customDataTestID: 'request-actions',
        actionItems: [
          {
            label: 'Resend Request',
            onClick: onResendRequest
          },
          {
            label: 'Change Provider',
            onClick: onChangeProvider
          }
        ]
      }
    ],
    [internalStates, onChangeProvider, onResendRequest, onStatusChange, states]
  );

  const handleTableChange = () => {};

  // don't render the table until all the static for the select menu is available
  if (!metaMisc || internalStates.size === 0) return null;

  return (
    <div
      style={{ minHeight: '500px', minWidth: '100%', maxHeight: '50vh', overflow: 'auto' }}
      data-testid="requests-table"
    >
      <TableDataView
        noDataFoundText="No imaging requests found"
        headerItems={ColumHeaders as HeaderItemType[]}
        hideColumnList={[]}
        dataSource={requests}
        handleTableChange={handleTableChange}
      />
    </div>
  );
};

export default CaseRequests;
